import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Select, Row, Col, Statistic, Space, message, Button, Modal } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import supplyChainService, { Shop, ReplenishmentRecommendation } from '../../services/supplyChainService';
import { InventoryAgeChart } from './components/Inventory/InventoryAgeChart';
import { ReplenishmentCalculator } from './components/Inventory/ReplenishmentCalculator';
import { ReplenishmentTable } from './components/Inventory/ReplenishmentTable';

const { Option } = Select;

const InventoryDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [shops, setShops] = useState<Shop[]>([]);
    const [selectedShop, setSelectedShop] = useState<number | undefined>(undefined);
    const [loading, setLoading] = useState(false);

    // Overview Data
    const [ageDistribution, setAgeDistribution] = useState<any[]>([]);
    const [ageSummary, setAgeSummary] = useState<any>({});

    // Replenishment Data
    const [replenishmentData, setReplenishmentData] = useState<ReplenishmentRecommendation[]>([]);
    const [targetDays, setTargetDays] = useState<number>(60);
    const [salesDays, setSalesDays] = useState<number>(30);
    const [replenishmentLoading, setReplenishmentLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    useEffect(() => { fetchShops(); }, []);
    useEffect(() => { fetchOverview(); }, [selectedShop]);

    const fetchShops = async () => {
        try {
            const response = await supplyChainService.getShops();
            setShops(response.data);
        } catch (error) { console.error('Failed to fetch shops', error); }
    };

    const fetchOverview = async () => {
        setLoading(true);
        try {
            const response = await supplyChainService.getInventoryAgeDistribution({ shop: selectedShop });
            setAgeDistribution(response.data.distribution);
            setAgeSummary(response.data.summary);
        } catch { message.error('获取库龄分布失败'); }
        finally { setLoading(false); }
    };

    const handleCalculateReplenishment = async () => {
        setReplenishmentLoading(true);
        try {
            const response = await supplyChainService.getReplenishmentAdvice({
                shop: selectedShop, target_days: targetDays, sales_days: salesDays
            });
            setReplenishmentData(response.data);
            message.success('计算完成');
        } catch { message.error('计算补货建议失败'); }
        finally { setReplenishmentLoading(false); }
    };

    const handleGeneratePO = async () => {
        if (selectedRowKeys.length === 0) return message.warning('请先选择要补货的SKU');
        const items = replenishmentData
            .filter(item => selectedRowKeys.includes(item.sku_code))
            .map(item => ({ seller_sku_code: item.sku_code, quantity: item.restock_needed > 0 ? item.restock_needed : 0 }))
            .filter(item => item.quantity > 0);

        if (items.length === 0) return message.warning('所选SKU无需补货 (建议补货量 <= 0)');

        Modal.confirm({
            title: '确认生成采购单?',
            content: `将为 ${items.length} 个SKU生成采购单。系统将根据SKU的主供应商自动拆分订单。`,
            onOk: async () => {
                try {
                    const res = await supplyChainService.generatePurchaseOrders(items);
                    message.success(`成功生成 ${res.data.count} 个采购单`);
                    navigate('/supply-chain/procurement/orders');
                    setSelectedRowKeys([]);
                } catch { message.error('生成失败'); }
            }
        });
    };

    return (
        <div style={{ padding: 24 }}>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col span={24}>
                    <Card title="控制台" extra={
                        <Space>
                            <Select style={{ width: 200 }} placeholder="选择店铺" allowClear onChange={setSelectedShop} value={selectedShop}>
                                {shops.map(shop => <Option key={shop.id} value={shop.id}>{shop.name}</Option>)}
                            </Select>
                            <Button icon={<ReloadOutlined />} onClick={fetchOverview}>刷新</Button>
                        </Space>
                    }>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Statistic title="总冗余数量 (预估)" value={ageSummary?.total_excess || 0} valueStyle={{ color: '#cf1322' }} />
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <InventoryAgeChart distribution={ageDistribution} loading={loading} />
                </Col>
                <Col span={12}>
                    <ReplenishmentCalculator
                        targetDays={targetDays} setTargetDays={setTargetDays}
                        salesDays={salesDays} setSalesDays={setSalesDays}
                        onCalculate={handleCalculateReplenishment} loading={replenishmentLoading}
                    />
                </Col>
            </Row>

            <div style={{ marginTop: 16 }}>
                <ReplenishmentTable
                    data={replenishmentData} loading={replenishmentLoading}
                    selectedRowKeys={selectedRowKeys} onSelectionChange={setSelectedRowKeys}
                    onGeneratePO={handleGeneratePO}
                />
            </div>
        </div>
    );
};

export default InventoryDashboard;
